/**
 * Gesture breakdown for a detected (or manually labeled) stroke:
 * phases, durations, joint angles at phase transitions, coaching cues.
 */
import { phasesOfStroke, FH_TOPSPIN_PHASE_ORDER } from "./poseHeuristics";
import { sampleNearest } from "./poseTimeline";
import {
  elbowAnglesForSide,
  kneeAnglesForSide,
} from "./strokeSlowMoKneeOverlay";
import { phaseDurationMs as labelPhaseDurationMs } from "./strokeLabelModel";

export const STROKE_ANALYSIS_PHASE_ORDER = FH_TOPSPIN_PHASE_ORDER;

export const STROKE_ANALYSIS_PHASE_LABEL = Object.freeze({
  preparation: "Preparation",
  forward_swing: "Forward swing",
  contact: "Contact",
  follow_through: "Follow-through",
  recovery: "Recovery",
  toss: "Toss",
  swing: "Swing",
});

export const STROKE_KIND_LABEL = Object.freeze({
  fh_topspin_left: "FH topspin",
  fh_left: "Forehand",
  bh_topspin_left: "BH topspin",
  bh_left: "Backhand",
  serve_toss: "Serve",
  labeled: "Labeled stroke",
});

const DETECTION_KEYS = [
  "fh_topspin_left",
  "fh_left",
  "bh_topspin_left",
  "bh_left",
];

function num(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function contactMs(stroke) {
  return (
    num(stroke?.t_contact_ms) ??
    num(stroke?.phases?.contact?.t_start_ms) ??
    num(stroke?.t_peak_ms) ??
    num(stroke?.t_ms)
  );
}

function phaseDuration(raw) {
  const fromLabel = labelPhaseDurationMs(raw);
  if (fromLabel != null) return fromLabel;
  const t0 = num(raw?.t_start_ms);
  const t1 = num(raw?.t_end_ms);
  if (t0 == null || t1 == null || t1 < t0) return null;
  return t1 - t0;
}

function leftPlayerFromSample(sample) {
  if (!sample || typeof sample !== "object") return null;
  if (sample.left) return sample.left;
  if (sample.joints || sample.joints_full || sample.angles) return sample;
  return null;
}

function anglesAtSample(sample) {
  const player = leftPlayerFromSample(sample);
  if (!player) {
    return {
      leftKnee: null,
      rightKnee: null,
      leftElbow: null,
      rightElbow: null,
    };
  }
  const knees = kneeAnglesForSide(player);
  const elbows = elbowAnglesForSide(player);
  return {
    leftKnee: knees.left,
    rightKnee: knees.right,
    leftElbow: elbows.left,
    rightElbow: elbows.right,
  };
}

/**
 * Flatten detections into a chronologically ordered list of stroke refs.
 */
export function collectAnalyzableStrokes(detections = {}) {
  const out = [];
  for (const kind of DETECTION_KEYS) {
    const det = detections?.[kind];
    const strokes = Array.isArray(det?.strokes) ? det.strokes : [];
    strokes.forEach((stroke, index) => {
      const t = contactMs(stroke);
      if (t == null) return;
      out.push({
        id: `${kind}-${index}`,
        kind,
        index: index + 1,
        tMs: t,
        stroke,
        detection: det,
      });
    });
  }
  out.sort((a, b) => a.tMs - b.tMs);
  return out;
}

export function findStrokeNearMs(entries, tMs, { maxDistMs = 2500 } = {}) {
  const list = Array.isArray(entries) ? entries : [];
  if (!list.length || !Number.isFinite(Number(tMs))) return null;
  const t = Number(tMs);
  let best = null;
  let bestDist = Infinity;
  for (const entry of list) {
    const dist = Math.abs(Number(entry.tMs) - t);
    if (dist < bestDist) {
      bestDist = dist;
      best = entry;
    }
  }
  if (!best || bestDist > maxDistMs) return null;
  return best;
}

function buildPhaseRows(stroke, samples) {
  const phases = phasesOfStroke(stroke) || stroke?.phases || {};
  const order = STROKE_ANALYSIS_PHASE_ORDER.filter((key) => phases?.[key]);
  const rows = [];
  for (const key of order) {
    const raw = phases[key];
    const t0 = num(raw?.t_start_ms);
    const t1 = num(raw?.t_end_ms);
    const durationMs = phaseDuration(raw);
    const sample =
      t0 != null && Array.isArray(samples)
        ? sampleNearest(samples, t0)
        : null;
    const angles = anglesAtSample(sample);
    rows.push({
      key,
      label: STROKE_ANALYSIS_PHASE_LABEL[key] || key,
      tStartMs: t0,
      tEndMs: t1,
      durationMs,
      angles,
      transition: {
        tMs: t0,
        angles,
      },
    });
  }
  return rows;
}

function tip(id, severity, title, detail) {
  return { id, severity, title, detail };
}

/**
 * Coaching cues from phase timing + left-player angles + stroke tags.
 */
export function coachingTipsForStroke(stroke, phaseRows) {
  const tips = [];
  const byKey = Object.fromEntries(
    (phaseRows || []).map((row) => [row.key, row])
  );
  const prep = byKey.preparation;
  const forward = byKey.forward_swing;
  const contact = byKey.contact;
  const follow = byKey.follow_through;
  const recovery = byKey.recovery;
  const tags = Array.isArray(stroke?.tags) ? stroke.tags : [];

  if (prep?.durationMs != null && prep.durationMs < 90) {
    tips.push(
      tip(
        "prep_short",
        "warn",
        "Short preparation",
        "Load earlier: give yourself more time in preparation before the forward swing."
      )
    );
  }
  if (prep?.durationMs != null && prep.durationMs > 420) {
    tips.push(
      tip(
        "prep_long",
        "info",
        "Long preparation",
        "Preparation is stretched — commit earlier into the forward swing to keep rhythm."
      )
    );
  }

  const prepKnee =
    prep?.angles?.leftKnee ?? prep?.angles?.rightKnee ?? null;
  if (prepKnee != null && prepKnee > 158) {
    tips.push(
      tip(
        "prep_knee",
        "warn",
        "Low knee bend in preparation",
        `Knees are nearly straight (~${prepKnee}°) at prep. Sit lower to store elastic energy.`
      )
    );
  }

  if (forward?.durationMs != null && forward.durationMs < 55) {
    tips.push(
      tip(
        "forward_short",
        "warn",
        "Abrupt forward swing",
        "The forward swing window is very short. Start the acceleration earlier toward contact."
      )
    );
  }

  const contactElbow =
    contact?.angles?.leftElbow ?? contact?.angles?.rightElbow ?? null;
  if (contactElbow != null && contactElbow < 95) {
    tips.push(
      tip(
        "contact_elbow_bent",
        "warn",
        "Elbow too bent at contact",
        `Elbow ~${contactElbow}° at contact. Extend the racket arm through the ball for a fuller stroke.`
      )
    );
  }
  if (contactElbow != null && contactElbow > 165) {
    tips.push(
      tip(
        "contact_elbow_locked",
        "info",
        "Arm almost locked at contact",
        `Elbow ~${contactElbow}°. Keep a soft hinge so you can brush and adjust on contact.`
      )
    );
  }

  if (
    follow?.durationMs != null &&
    forward?.durationMs != null &&
    follow.durationMs + 20 < forward.durationMs * 0.55
  ) {
    tips.push(
      tip(
        "follow_short",
        "warn",
        "Cut follow-through",
        "Finish higher and longer after contact — the follow-through is short versus the forward swing."
      )
    );
  }

  if (recovery?.durationMs != null && recovery.durationMs > 700) {
    tips.push(
      tip(
        "recovery_long",
        "info",
        "Slow recovery",
        "Return to a ready stance sooner so you can cover the next ball."
      )
    );
  }

  if (tags.includes("missing_trunk_rotation")) {
    tips.push(
      tip(
        "trunk",
        "warn",
        "Limited trunk rotation",
        "Drive rotation from the hips and trunk into the stroke, not only the arm."
      )
    );
  }

  if (tags.includes("bad_placement") || tags.includes("off_table")) {
    tips.push(
      tip(
        "placement",
        "warn",
        "Contact timing / placement",
        "Contact looks mistimed relative to your usual band — pause at contact and check bat angle."
      )
    );
  }

  if (!tips.length) {
    tips.push(
      tip(
        "solid",
        "ok",
        "Solid phase pattern",
        "Timing and joint angles look balanced for this stroke. Compare a few neighbors for consistency."
      )
    );
  }

  return tips;
}

/**
 * Full analysis payload for the UI.
 */
export function analyzeStrokeGesture({
  stroke,
  kind = "fh_topspin_left",
  index = 1,
  samples = null,
} = {}) {
  if (!stroke || typeof stroke !== "object") return null;
  const phaseRows = buildPhaseRows(stroke, samples);
  if (!phaseRows.length) return null;

  const totalMs = phaseRows.reduce(
    (sum, row) => sum + (row.durationMs || 0),
    0
  );
  const phases = phaseRows.map((row) => ({
    ...row,
    pct: totalMs > 0 && row.durationMs != null
      ? Math.round((row.durationMs / totalMs) * 100)
      : null,
  }));

  const tStart = phases.reduce(
    (min, row) =>
      row.tStartMs != null ? Math.min(min, row.tStartMs) : min,
    Infinity
  );
  const tEnd = phases.reduce(
    (max, row) =>
      row.tEndMs != null ? Math.max(max, row.tEndMs) : max,
    -Infinity
  );

  return {
    kind,
    kindLabel: STROKE_KIND_LABEL[kind] || kind,
    index,
    tContactMs: contactMs(stroke),
    tStartMs: Number.isFinite(tStart) ? tStart : null,
    tEndMs: Number.isFinite(tEnd) ? tEnd : null,
    totalDurationMs: totalMs > 0 ? totalMs : null,
    angleDeg: num(stroke?.angle_deg),
    valleyAngleDeg: num(stroke?.valley_angle_deg),
    amplitudeMs: num(stroke?.amplitude_ms),
    tags: Array.isArray(stroke?.tags) ? stroke.tags : [],
    phases,
    tips: coachingTipsForStroke(stroke, phases),
    stroke,
  };
}

export function analyzeStrokeNearPlayhead({
  detections,
  samples,
  currentTimeSec,
  maxDistMs = 2500,
} = {}) {
  const entries = collectAnalyzableStrokes(detections);
  const tMs = Math.round(Math.max(0, Number(currentTimeSec) || 0) * 1000);
  const entry = findStrokeNearMs(entries, tMs, { maxDistMs });
  if (!entry) {
    return { entries, entry: null, analysis: null };
  }
  const analysis = analyzeStrokeGesture({
    stroke: entry.stroke,
    kind: entry.kind,
    index: entry.index,
    samples,
  });
  return { entries, entry, analysis };
}

/** Stable fingerprint so we skip DB writes when detections are unchanged. */
export function strokeAnalysisSourceSignature(entries = []) {
  const list = Array.isArray(entries) ? entries : [];
  if (!list.length) return "empty";
  return list
    .map((e) => `${e.kind}:${e.index}:${Math.round(Number(e.tMs) || 0)}`)
    .join("|");
}

/** Drop heavy raw stroke blobs before persisting. */
export function serializeStrokeAnalysisForStorage(analysis) {
  if (!analysis || typeof analysis !== "object") return null;
  const { stroke: _stroke, ...rest } = analysis;
  return {
    ...rest,
    phases: Array.isArray(analysis.phases)
      ? analysis.phases.map((phase) => ({
          key: phase.key,
          label: phase.label,
          tStartMs: phase.tStartMs,
          tEndMs: phase.tEndMs,
          durationMs: phase.durationMs,
          pct: phase.pct,
          angles: phase.angles || null,
          transition: phase.transition
            ? {
                tMs: phase.transition.tMs,
                angles: phase.transition.angles || null,
              }
            : null,
        }))
      : [],
    tips: Array.isArray(analysis.tips)
      ? analysis.tips.map((tip) => ({
          id: tip.id,
          severity: tip.severity,
          title: tip.title,
          detail: tip.detail,
        }))
      : [],
  };
}

/**
 * Build the DB payload for every detected stroke on this clip.
 */
export function buildStrokeAnalysisDocument({
  detections,
  samples,
  poseJobId = null,
} = {}) {
  const entries = collectAnalyzableStrokes(detections || {});
  const strokes = [];
  for (const entry of entries) {
    const analysis = analyzeStrokeGesture({
      stroke: entry.stroke,
      kind: entry.kind,
      index: entry.index,
      samples,
    });
    if (!analysis) continue;
    strokes.push({
      id: entry.id,
      kind: entry.kind,
      index: entry.index,
      tMs: entry.tMs,
      analysis: serializeStrokeAnalysisForStorage(analysis),
    });
  }
  return {
    version: 1,
    pose_job_id: poseJobId || null,
    source_signature: strokeAnalysisSourceSignature(entries),
    stroke_count: strokes.length,
    strokes,
  };
}
